import React, { useContext, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card, CardActions, CardContent, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getBgImage, getLogo } from '../../api/aws/s3';
import {
  companiesRef,
  editPublisherCompany
} from '../../api/firebase/firestore/company';
import { publisherRegisterEditSchema } from '../../api/firebase/firestore/company/validation';
import { editPublisherUser, usersRef } from '../../api/firebase/firestore/user';
import {
  black,
  blackText,
  primaryButtonBackground
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  textFontSize,
  textMb,
  sizeBelowTenMb,
  titleMb
} from '../../constants/Location';
import { prefecture, publisherIndustry, sales } from '../../constants/Select';
import { screenType, select } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomImageUploader from './CustomImageUploader';
import CustomLoading from './CustomLoading';
import CustomRedioButton from './CustomRadioButton';
import CustomSelectField from './CustomSelectField';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';

import type { AxiosResponse } from 'axios';
import type { DocumentData } from 'firebase/firestore';

const styles = {
  blackText,
  buttonFont,
  cardContentPadding,
  cardSize,
  textFontSize,
  textMb,
  titleMb,
  wrapper: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'column'
    },
    display: 'flex',
    margin: '0 auto'
  },
  zipcodeButton: {
    '&:hover': {
      backgroundColor: black
    },
    backgroundColor: black,
    height: '30px',
    marginBottom: '25px',
    marginLeft: '16px',
    minWidth: '87px'
  }
};

interface PublisherRegisterBasicType {
  bgImg: File;
  logo: File;
  companyName: string;
  companyUrl: string;
  industry: number | undefined | null;
  industryOther: string | undefined | null;
  introduction: string;
  representativeName: string;
  president: string;
  foundationYear: number;
  listedFlg: boolean;
  zipcode: string;
  prefecture: number;
  city: string;
  address: string;
  capital: number;
  sales: number;
  employees: number;
  resources_0: string;
  resources_1: string;
  resources_2: string;
  resources_3: string;
  resources_4: string;
  sns_0: string;
  sns_1: string;
  sns_2: string;
  sns_3: string;
  sns_4: string;
}

interface Address {
  results: [
    {
      address1: string;
      address2: string;
      address3: string;
    }
  ];
}

interface Props {
  screen: number;
}

const PublisherRegisterBasicCard: React.FC<Props> = (props) => {
  const { screen } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [userData, setUserData] = useState<DocumentData>();
  const [companyData, setCompanyData] = useState<DocumentData>();
  const widthHalf = '50%';
  const industryList = publisherIndustry();
  const salesList = sales();
  const [open, setOpen] = useState<boolean>(false);
  const [errorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);

  useEffect(() => {
    document.title = t('title.publisher.mypageCompanyEdit');
  }, []);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('companyName') === undefined ||
        '' ||
        watch('companyUrl') === undefined ||
        '' ||
        watch('industry') === undefined ||
        (watch('industry') === select.other &&
          Boolean(!watch('industryOther'))) ||
        watch('introduction') === undefined ||
        '' ||
        watch('representativeName') === undefined ||
        '' ||
        watch('foundationYear') === undefined ||
        '' ||
        watch('listedFlg') === undefined ||
        watch('zipcode') === undefined ||
        '' ||
        watch('prefecture') === undefined ||
        watch('city') === undefined ||
        Boolean(errors.logo) ||
        Boolean(logoSizeError) ||
        Boolean(errors.bgImg) ||
        Boolean(bgImageSizeError) ||
        Boolean(errors.companyName) ||
        Boolean(errors.companyUrl) ||
        Boolean(errors.industry) ||
        (watch('industry') === select.other && Boolean(errors.industryOther)) ||
        Boolean(errors.introduction) ||
        Boolean(errors.representativeName) ||
        Boolean(errors.president) ||
        Boolean(errors.foundationYear) ||
        Boolean(errors.listedFlg) ||
        Boolean(errors.zipcode) ||
        Boolean(errors.prefecture) ||
        Boolean(errors.city) ||
        Boolean(errors.address) ||
        Boolean(errors.capital) ||
        Boolean(errors.sales) ||
        Boolean(errors.employees) ||
        Boolean(errors.resources_0) ||
        Boolean(errors.resources_1) ||
        Boolean(errors.resources_2) ||
        Boolean(errors.resources_3) ||
        Boolean(errors.resources_4) ||
        Boolean(errors.sns_0) ||
        Boolean(errors.sns_1) ||
        Boolean(errors.sns_2) ||
        Boolean(errors.sns_3) ||
        Boolean(errors.sns_4) ||
        Boolean(employeesError)
      );
    }
    return false;
  };

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm<PublisherRegisterBasicType>({
    defaultValues: {
      industry: 0,
      listedFlg: false,
      prefecture: 0,
      sales: 0
    },
    resolver: yupResolver(publisherRegisterEditSchema)
  });

  // 登録済データの取得
  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const userDocRed = doc(usersRef, currentUser.uid);
        const userDocSnap = await getDoc(userDocRed);
        const companyId = userDocSnap.data()?.companyId as string;
        const companyDocRed = doc(companiesRef, companyId);
        const companySnap = await getDoc(companyDocRed);
        const bgImg = await getBgImage(companySnap);
        const logoId = await getLogo(companySnap);
        setUserData(userDocSnap.data());
        setCompanyData(companySnap.data());
        setPreviewBgIm(bgImg);
        setPreviewLogo(logoId);
      }
    })();
  }, []);

  // 編集時の初期値のセット
  useEffect(() => {
    if (companyData && userData) {
      reset({
        address: companyData.address as string,
        bgImg: companyData.bgImg as File,
        capital: companyData.capital as number,
        city: companyData.city as string,
        companyName: companyData.name as string,
        companyUrl: companyData.url as string,
        employees: companyData.employees as number,
        foundationYear: companyData.foundationYear as number,
        industry: (companyData.industry as number) ?? 0,
        industryOther: companyData.industryOther as string,
        introduction: companyData.introduction as string,
        listedFlg: companyData.listedFlg as boolean,
        logo: companyData.logo as File,
        prefecture: companyData.prefecture as number,
        president: companyData.president as string,
        representativeName: userData.name as string,
        resources_0: (companyData.pressReleases as string)[0],
        resources_1: (companyData.pressReleases as string)[1],
        resources_2: (companyData.pressReleases as string)[2],
        resources_3: (companyData.pressReleases as string)[3],
        resources_4: (companyData.pressReleases as string)[4],
        sales: (companyData.sales as number) ?? 0,
        sns_0: (companyData.snsAccount as string)[0],
        sns_1: (companyData.snsAccount as string)[1],
        sns_2: (companyData.snsAccount as string)[2],
        sns_3: (companyData.snsAccount as string)[3],
        sns_4: (companyData.snsAccount as string)[4],
        zipcode: companyData.zipcode as string
      });
    }
  }, [companyData]);

  // 選択を変更したときの処理
  useEffect(() => {
    if (watch('industry') !== select.other) {
      setValue('industryOther', null);
    }
  }, [watch('industry')]);

  const prefectureList = prefecture();
  const getPrefecture = (value: string): number => {
    const data = prefectureList.find(
      (element): boolean => element.option === value
    );
    return data ? data.value : 0;
  };

  const [previewBgImg, setPreviewBgIm] = useState('');
  const [previewLogo, setPreviewLogo] = useState('');
  const [s3FileBgImg, setS3FileBgImg] = useState<FileList | null>(null);
  const [s3FileLogo, setS3FileLogo] = useState<FileList | null>(null);
  const [logoSizeError, setLogoSizeError] = useState<string | null>(null);
  const [bgImageSizeError, setBgImageSizeError] = useState<string | null>(null);

  const bgImageValidationCheck = (): boolean => {
    if (s3FileBgImg && s3FileBgImg.length > 0) {
      if (sizeBelowTenMb(s3FileBgImg[0].size)) {
        setBgImageSizeError(t('validation.error.imageSize'));
        return true;
      }
      setBgImageSizeError(null);
      return false;
    }
    setBgImageSizeError(null);
    return false;
  };

  useEffect(() => {
    if (firstSubmit) {
      bgImageValidationCheck();
    } else {
      setBgImageSizeError(null);
    }
  }, [s3FileBgImg]);

  const logoValidationCheck = (): boolean => {
    if (s3FileLogo && s3FileLogo.length > 0) {
      if (sizeBelowTenMb(s3FileLogo[0].size)) {
        setLogoSizeError(t('validation.error.imageSize'));
        return true;
      }
      setLogoSizeError(null);
      return false;
    }
    setLogoSizeError(null);
    return false;
  };

  useEffect(() => {
    if (firstSubmit) {
      logoValidationCheck();
    } else {
      setLogoSizeError(null);
    }
  }, [s3FileLogo]);

  const changeUploadBgImgFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setPreviewBgIm(window.URL.createObjectURL(blob));
      setS3FileBgImg(file);
    }
  };

  const changeUploadLogoFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setPreviewLogo(window.URL.createObjectURL(blob));
      setS3FileLogo(file);
    }
  };

  const updateZipcodeSub = async (): Promise<void> => {
    try {
      const res: AxiosResponse<Address> = await axios.get(
        'https://zipcloud.ibsnet.co.jp/api/search',
        {
          params: {
            zipcode: watch('zipcode')
          }
        }
      );
      if (res.data.results) {
        const result = res.data.results[0];
        const prefectureNumber: number = getPrefecture(result.address1);
        const city: string = result.address2;
        const address: string = result.address3;
        setValue('prefecture', prefectureNumber);
        setValue('city', city);
        errors.city && clearErrors('city');
        setValue('address', address);
      }
    } catch (e) {
      throwError(e);
    }
  };

  const [employeesError, setEmployeesError] = useState<string | null>(null);

  const employeesValidationCheck = (): boolean => {
    if (
      watch('employees') === null ||
      watch('employees') === undefined ||
      Number.isNaN(watch('employees'))
    )
      return false;
    if (Number(watch('employees')) >= 100000000) {
      setEmployeesError(t('validation.error.below100000000'));
      return true;
    } else if (Number(watch('employees')) < 0) {
      setEmployeesError(t('validation.error.above1'));
      return true;
    }
    setEmployeesError(null);
    return false;
  };

  useEffect(() => {
    if (firstSubmit) {
      employeesValidationCheck();
    } else {
      setEmployeesError(null);
    }
  }, [watch('employees')]);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      // AuthUserの作成
      if (currentUser) {
        // Usersコレクションの更新
        await editPublisherUser(currentUser.uid, watch);
        // Companiesコレクションにデータを追加
        if (screen === screenType.publisher) {
          await editPublisherCompany(
            currentUser.uid,
            watch,
            s3FileBgImg,
            s3FileLogo
          );
        }
        // 登録完了画面へ遷移
        navigate('/publisher/mypage/company');
      } else {
        throw new Error();
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.wrapper}>
        <Typography component="div" sx={{ marginTop: '24px' }}>
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <Grid>
              <Card sx={[styles.cardSize, { mb: '24px' }]}>
                <CardContent sx={styles.cardContentPadding}>
                  <Controller
                    render={({ field }) => (
                      <CustomImageUploader
                        {...field}
                        type="bg"
                        preview={previewBgImg}
                        error={Boolean(errors.bgImg)}
                        otherError={bgImageSizeError}
                        helperText={errors.bgImg?.message}
                        onChange={(
                          value: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          field.onChange(value);
                          changeUploadBgImgFile(value);
                        }}
                      />
                    )}
                    control={control}
                    name="bgImg"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomImageUploader
                        {...field}
                        type="logo"
                        preview={previewLogo}
                        error={Boolean(errors.logo)}
                        otherError={logoSizeError}
                        helperText={errors.logo?.message}
                        onChange={(
                          value: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          field.onChange(value);
                          changeUploadLogoFile(value);
                        }}
                      />
                    )}
                    control={control}
                    name="logo"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.companyName)}
                        helperText={errors.companyName?.message}
                        labelText={t('registerBasic.companyName')}
                        requiredText={t('form.required')}
                        isRequired
                        placeholder={t('common.enbro')}
                      />
                    )}
                    control={control}
                    name="companyName"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.companyUrl)}
                        helperText={errors.companyUrl?.message}
                        labelText={t('form.companyUrl')}
                        requiredText={t('form.required')}
                        isRequired
                        placeholder="https://en-broker.co.jp"
                        type="url"
                      />
                    )}
                    control={control}
                    name="companyUrl"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomSelectField
                        {...field}
                        labelText={t('common.industryType')}
                        requiredText={t('form.required')}
                        isRequired
                        blank
                        defaultValue={0}
                        list={industryList}
                        sx={{ width: '400px' }}
                        {...(watch('industry') === 99 && { formMb: 'narrow' })}
                      />
                    )}
                    control={control}
                    name="industry"
                  />
                  {watch('industry') === 99 && (
                    <Controller
                      render={({ field }) => (
                        <CustomTextfield
                          {...field}
                          error={
                            watch('industry') === select.other &&
                            Boolean(errors.industryOther)
                          }
                          helperText={errors.industryOther?.message}
                          isRequired={false}
                          placeholder={t('form.departmentOther')}
                          sx={{ marginTop: '0px' }}
                        />
                      )}
                      control={control}
                      name="industryOther"
                    />
                  )}
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.introduction)}
                        helperText={errors.introduction?.message}
                        labelText={t('form.introduction')}
                        requiredText={t('form.required')}
                        isRequired
                        multiline
                        rows={7}
                        fieldType="textareaRow7"
                        placeholder={t('form.introductionPlaceholder')}
                      />
                    )}
                    control={control}
                    name="introduction"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.representativeName)}
                        helperText={errors.representativeName?.message}
                        labelText={t('registerBasic.representativeName')}
                        requiredText={t('form.required')}
                        isRequired
                        placeholder={t('common.name')}
                        width={widthHalf}
                      />
                    )}
                    control={control}
                    name="representativeName"
                  />

                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.president)}
                        helperText={errors.president?.message}
                        labelText={t('registerBasic.leaderName')}
                        isRequired={false}
                        placeholder={t('common.name')}
                        width={widthHalf}
                      />
                    )}
                    control={control}
                    name="president"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.foundationYear)}
                        helperText={errors.foundationYear?.message}
                        labelText={t('common.yearEstablished')}
                        requiredText={t('form.required')}
                        isRequired
                        placeholder={t('common.yearEx')}
                        width={widthHalf}
                        type="number"
                      />
                    )}
                    control={control}
                    name="foundationYear"
                  />

                  <Controller
                    render={({ field }) => (
                      <CustomRedioButton
                        {...field}
                        error={Boolean(errors.listedFlg)}
                        helperText={errors.listedFlg?.message}
                        labelText={t('registerBasic.listingOrNot')}
                        requiredText={t('form.required')}
                        isRequired
                        trueOption={t('registerBasic.listing')}
                        falseOption={t('registerBasic.Notlisting')}
                      />
                    )}
                    control={control}
                    name="listedFlg"
                  />

                  <Typography sx={{ alignItems: 'center', display: 'flex' }}>
                    <Controller
                      render={({ field }) => (
                        <CustomTextfield
                          {...field}
                          error={Boolean(errors.zipcode)}
                          helperText={errors.zipcode?.message}
                          labelText={t('form.zipcode')}
                          requiredText={t('form.required')}
                          isRequired
                          width="narrow"
                          placeholder="0000000"
                          type="number"
                          underText={t('form.zipcodeGuideText')}
                        />
                      )}
                      control={control}
                      name="zipcode"
                    />
                    <Button
                      component="label"
                      variant="contained"
                      sx={styles.zipcodeButton}
                      onClick={() => {
                        void (async () => {
                          await updateZipcodeSub();
                        })();
                      }}
                    >
                      {t('form.getAddress')}
                    </Button>
                  </Typography>
                  <Controller
                    render={({ field }) => (
                      <CustomSelectField
                        {...field}
                        error={Boolean(errors.prefecture)}
                        helperText={errors.prefecture?.message}
                        labelText={t('form.prefecture')}
                        requiredText={t('form.required')}
                        isRequired
                        width="narrow"
                        list={prefecture()}
                        blank
                      />
                    )}
                    control={control}
                    name="prefecture"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.city)}
                        helperText={errors.city?.message}
                        labelText={t('form.city')}
                        requiredText={t('form.required')}
                        isRequired
                      />
                    )}
                    control={control}
                    name="city"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.address)}
                        helperText={errors.address?.message}
                        labelText={t('form.address')}
                        isRequired={false}
                      />
                    )}
                    control={control}
                    name="address"
                  />

                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.capital)}
                        helperText={errors.capital?.message}
                        labelText={t('registerBasic.capital')}
                        isRequired={false}
                      />
                    )}
                    control={control}
                    name="capital"
                  />

                  <Controller
                    render={({ field }) => (
                      <CustomSelectField
                        {...field}
                        error={Boolean(errors.sales)}
                        helperText={errors.sales?.message}
                        labelText={t('registerBasic.market')}
                        blank
                        defaultValue={0}
                        isRequired={false}
                        list={salesList}
                        sx={{ width: '400px' }}
                      />
                    )}
                    control={control}
                    name="sales"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.employees) ?? employeesError}
                        otherError={employeesError}
                        helperText={errors.employees?.message}
                        labelText={t('registerBasic.numbers')}
                        isRequired={false}
                        width={widthHalf}
                      />
                    )}
                    control={control}
                    name="employees"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.resources_0)}
                        helperText={errors.resources_0?.message}
                        labelText={t('registerBasic.resources')}
                        requiredText={t('form.required')}
                        isRequired={false}
                        formMb="narrow"
                        type="url"
                      />
                    )}
                    control={control}
                    name="resources_0"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.resources_1)}
                        helperText={errors.resources_1?.message}
                        isRequired={false}
                        formMb="narrow"
                        type="url"
                      />
                    )}
                    control={control}
                    name="resources_1"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.resources_2)}
                        helperText={errors.resources_2?.message}
                        isRequired={false}
                        formMb="narrow"
                        type="url"
                      />
                    )}
                    control={control}
                    name="resources_2"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.resources_3)}
                        helperText={errors.resources_3?.message}
                        isRequired={false}
                        formMb="narrow"
                        type="url"
                      />
                    )}
                    control={control}
                    name="resources_3"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.resources_4)}
                        helperText={errors.resources_4?.message}
                        isRequired={false}
                        type="url"
                      />
                    )}
                    control={control}
                    name="resources_4"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.sns_0)}
                        helperText={errors.sns_0?.message}
                        labelText={t('registerBasic.accounts')}
                        requiredText={t('form.required')}
                        isRequired={false}
                        placeholder="facebook"
                        formMb="narrow"
                        type="url"
                      />
                    )}
                    control={control}
                    name="sns_0"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.sns_1)}
                        helperText={errors.sns_1?.message}
                        isRequired={false}
                        placeholder="Twitter"
                        formMb="narrow"
                        type="url"
                      />
                    )}
                    control={control}
                    name="sns_1"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.sns_2)}
                        helperText={errors.sns_2?.message}
                        isRequired={false}
                        placeholder="Instagram"
                        formMb="narrow"
                        type="url"
                      />
                    )}
                    control={control}
                    name="sns_2"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.sns_3)}
                        helperText={errors.sns_3?.message}
                        isRequired={false}
                        placeholder="TikTok"
                        formMb="narrow"
                        type="url"
                      />
                    )}
                    control={control}
                    name="sns_3"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.sns_4)}
                        helperText={errors.sns_4?.message}
                        isRequired={false}
                        placeholder="note"
                        formMb="narrow"
                        type="url"
                      />
                    )}
                    control={control}
                    name="sns_4"
                  />
                  <CardActions
                    sx={{
                      '&.MuiCardActions-root': {
                        marginTop: '30px'
                      },
                      padding: 0
                    }}
                  >
                    <Grid container direction="column" alignItems="center">
                      <Button
                        disabled={buttonDisabledFlag()}
                        variant="contained"
                        sx={[
                          primaryButtonBackground,
                          {
                            '@media screen and (max-width:960px)': {
                              height: '42px'
                            },
                            height: '42px',
                            width: '100%'
                          }
                        ]}
                        onClick={() => {
                          void (async () => {
                            setFirstSubmit(true);
                            await handleSubmit(async () => {
                              const bgImageCheck = bgImageValidationCheck();
                              const logoCheck = logoValidationCheck();
                              const emplyeesCheck = employeesValidationCheck();
                              if (
                                !bgImageCheck &&
                                !logoCheck &&
                                !emplyeesCheck
                              ) {
                                await onSubmit();
                              }
                            })();
                          })();
                        }}
                      >
                        <Typography
                          component="div"
                          color="white"
                          sx={{ fontSize: 15 }}
                        >
                          {t('registerBasic.editSave')}
                        </Typography>
                      </Button>
                    </Grid>
                  </CardActions>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Typography>
      </Typography>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default PublisherRegisterBasicCard;
